import { api, publicApi } from '@/api'
import { BaseApi } from '@/api/BaseApi.ts'
import type {
  ChildFeatureData,
  ChildrenOrderingData,
  FeatureData,
  FeatureDuplicateData,
  FeatureFetchData,
  FeatureFetchParams,
  FeatureFormData,
  FeatureLinkData,
  FeatureListItemData,
  LinkCollectBody,
  LinkCollectData,
  LinkFeatureData,
  UserPreferenceBody,
  UserPreferenceData,
} from '@/api/Feature/types.ts'
import type { FileUploadData, FileUploadResponse } from '@/api/File/types.ts'
import type { ApiFile, Paginated, ParamType } from '@/api/types.ts'

class Api extends BaseApi<
  FeatureData,
  Paginated<FeatureFetchData>,
  FeatureFormData,
  FeatureFetchParams
> {
  readonly baseUrl = '/initiatives/:initiativeId/features'

  async fetchChildren(params: ParamType[] = []): Promise<ChildFeatureData[]> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:featureId/children', params)}`
    const { data } = await api.get<ChildFeatureData[]>(url, this.config)
    return data
  }

  async removeChild(params: ParamType[] = []): Promise<void> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:featureId/children/:childId', params)}`
    const { data } = await api.delete<void>(url, this.config)
    return data
  }

  async linkChild(
    params: ParamType[],
    payload: LinkFeatureData
  ): Promise<void> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:featureId/children', params)}`
    const { data } = await api.post<void>(url, payload, this.config)
    return data
  }

  async updateChildOrdering(
    params: ParamType[],
    payload: ChildrenOrderingData
  ): Promise<void> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:featureId/children/ordering', params)}`
    const { data } = await api.put<void>(url, payload, this.config)
    return data
  }

  async linkCollect(
    params: ParamType[],
    payload: LinkCollectBody
  ): Promise<void> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:featureId/children/:childId/collects', params)}`
    const { data } = await api.post<void>(url, payload, this.config)
    return data
  }

  async fetchLinkedCollects(params: ParamType[]): Promise<LinkCollectData[]> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:featureId/children/collects/:collectId', params)}`
    const { data } = await api.get<LinkCollectData[]>(url, this.config)
    return data
  }

  async fetchByLink(key: string): Promise<FeatureLinkData> {
    const url = `/collect/${key}`
    const { data } = await publicApi.post<FeatureLinkData>(url, {}, this.config)
    return data
  }

  async duplicateFeature(
    params: ParamType[],
    payload: FeatureDuplicateData
  ): Promise<{ id: number }> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:featureId/duplicate', params)}`
    const { data } = await api.post<{ id: number }>(url, payload, this.config)
    return data
  }

  async removeFeature(params: ParamType[] = []): Promise<void> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:featureId', params)}`
    const { data } = await api.delete<void>(url, this.config)
    return data
  }

  async saveUserPreference(
    params: ParamType[],
    payload: UserPreferenceBody
  ): Promise<void> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:featureId/user-preferences', params)}`
    const { data } = await api.post<void>(url, payload, this.config)
    return data
  }

  async getUserPreference(
    params: ParamType[],
    key: string
  ): Promise<UserPreferenceData> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:featureId/user-preferences/' + key, params)}`
    const { data } = await api.get<UserPreferenceData>(url, this.config)
    return data
  }

  async savePreference(
    params: ParamType[],
    payload: UserPreferenceBody
  ): Promise<void> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:featureId/preferences', params)}`
    const { data } = await api.post<void>(url, payload, this.config)
    return data
  }

  async getPreference(
    params: ParamType[],
    key: string
  ): Promise<UserPreferenceData> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:featureId/preferences/' + key, params)}`
    const { data } = await api.get<UserPreferenceData>(url, this.config)
    return data
  }

  async uploadFileByField(
    params: ParamType[],
    { file, onUploadProgress }: FileUploadData
  ): Promise<FileUploadResponse> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:featureId/fields/:fieldAlias/files', params)}`
    const { data } = await api.post<FileUploadResponse>(url, file, {
      headers: { 'content-type': 'multipart/form-data' },
      onUploadProgress,
    })
    return data
  }

  async getFileByField(
    params: ParamType[],
    fileUUID: string
  ): Promise<Required<ApiFile>> {
    const url = `${this.getBaseUrl(this.baseUrl + '/:featureId/fields/:fieldAlias/files/' + fileUUID, params)}`
    const { data } = await api.get<Required<ApiFile>>(url, this.config)
    return data
  }

  async fetchSummary(
    params: ParamType[],
    queryParams: FeatureFetchParams = {}
  ): Promise<Paginated<FeatureListItemData>> {
    const URParams = new URLSearchParams(this.buildFetchParams(queryParams))
    const url = `${this.getBaseUrl(this.baseUrl, params)}?${URParams.toString()}`
    const { data } = await api.get<Paginated<FeatureListItemData>>(
      url,
      this.config
    )
    return data
  }
}

const FeatureApi = new Api()

export default FeatureApi
